import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Clock, Edit2 } from 'lucide-react';
import { Service, ServiceAddon, updateService, deleteService, getServiceAddons, updateServiceAddon, deleteServiceAddon } from '../lib/services';
import { FlatRateTemplate, getFlatRateTemplatesByService, deleteFlatRateTemplate } from '../lib/flatRateTemplates';
import FlatRateHoursModal from './FlatRateHoursModal';
import AddAddonModal from './AddAddonModal';

interface ServiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null;
  onUpdate: () => void;
}

const colorOptions = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Teal', value: '#14b8a6' }
];

export default function ServiceDetailsModal({ 
  isOpen, 
  onClose, 
  service, 
  onUpdate 
}: ServiceDetailsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState<{ info?: boolean; pricing?: boolean; addons?: boolean; templates?: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({});
  const [selectedColor, setSelectedColor] = useState(colorOptions[0].value);
  const [priceOptions, setPriceOptions] = useState<{
    [key: string]: { name: string; price: number; duration: { hours: number; minutes: number } }
  }>({});
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [editingAddon, setEditingAddon] = useState<string | null>(null);
  const [editedAddonData, setEditedAddonData] = useState<Partial<ServiceAddon>>({});
  const [isFlatRateModalOpen, setIsFlatRateModalOpen] = useState(false);
  const [isAddAddonModalOpen, setIsAddAddonModalOpen] = useState(false);
  const [templates, setTemplates] = useState<FlatRateTemplate[]>([]);
  const [hasInfoChanges, setHasInfoChanges] = useState(false);
  const [hasPricingChanges, setHasPricingChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Partial<Service>>({});
  const [originalPriceOptions, setOriginalPriceOptions] = useState<any>({});

  useEffect(() => {
    if (isOpen && service) {
      setIsAnimating(true);
      setFormData(service);
      setOriginalFormData({...service});
      setSelectedColor(service.color || colorOptions[0].value);
      
      // Initialize price options from service data
      const prices = {};
      service.price_options?.forEach(po => {
        const hours = Math.floor(po.duration_minutes / 60);
        const minutes = po.duration_minutes % 60;
        prices[po.id || po.name] = {
          name: po.name || 'Default',
          price: po.price,
          duration: { hours, minutes }
        };
      });

      // Ensure at least one price option exists
      if (Object.keys(prices).length === 0) {
        prices['default'] = {
          name: 'Default',
          price: 0,
          duration: { hours: 1, minutes: 0 }
        };
      }

      setPriceOptions(prices);
      setOriginalPriceOptions(JSON.parse(JSON.stringify(prices)));
      setHasInfoChanges(false);
      setHasPricingChanges(false);

      // Load add-ons and templates
      loadAddons();
      loadTemplates();
    }
  }, [isOpen, service]);

  useEffect(() => {
    if (Object.keys(originalFormData).length > 0) {
      setHasInfoChanges(
        formData.name !== originalFormData.name ||
        formData.description !== originalFormData.description ||
        formData.category_id !== originalFormData.category_id
      );
    }
  }, [formData, originalFormData]);

  useEffect(() => {
    if (Object.keys(originalPriceOptions).length > 0) {
      // Check if keys are different
      const originalKeys = Object.keys(originalPriceOptions);
      const currentKeys = Object.keys(priceOptions);
      
      if (originalKeys.length !== currentKeys.length) {
        setHasPricingChanges(true);
        return;
      }
      
      // Check if any options have changed
      let hasChanges = false;
      for (const key of currentKeys) {
        if (!originalPriceOptions[key] || 
            priceOptions[key].name !== originalPriceOptions[key].name ||
            priceOptions[key].price !== originalPriceOptions[key].price ||
            priceOptions[key].duration.hours !== originalPriceOptions[key].duration.hours ||
            priceOptions[key].duration.minutes !== originalPriceOptions[key].duration.minutes) {
          hasChanges = true;
          break;
        }
      }
      
      setHasPricingChanges(hasChanges);
    }
  }, [priceOptions, originalPriceOptions]);

  const loadAddons = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      const addonsData = await getServiceAddons(service.id);
      setAddons(addonsData);
    } catch (err) {
      console.error('Error loading add-ons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load add-ons');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  const loadTemplates = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, templates: true }));
      const templatesData = await getFlatRateTemplatesByService(service.id);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleSaveInfo = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, info: true }));
      setError(null);

      const updates: Partial<Service> = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        color: selectedColor,
        tax_exempt: formData.tax_exempt
      };

      await updateService(service.id, updates);
      await onUpdate();
    } catch (err) {
      console.error('Error updating service info:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service information');
    } finally {
      setLoading(prev => ({ ...prev, info: false }));
    }
  };

  const handleSavePricing = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, pricing: true }));
      setError(null);

      // Convert price options to the format expected by the API
      const priceOptionUpdates = Object.entries(priceOptions).map(([id, option]) => ({
        name: option.name || 'Default',
        price: option.price,
        duration_minutes: (option.duration.hours * 60) + option.duration.minutes
      }));

      await updateService(service.id, {
        price_options: priceOptionUpdates
      });
      await onUpdate();
    } catch (err) {
      console.error('Error updating service pricing:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service pricing');
    } finally {
      setLoading(prev => ({ ...prev, pricing: false }));
    }
  };

  const handleAddPrice = () => {
    const newId = `new-${Date.now()}`;
    setPriceOptions(prev => ({
      ...prev,
      [newId]: { 
        name: 'New Option',
        price: 0, 
        duration: { hours: 1, minutes: 0 } 
      }
    }));
  };

  const handleUpdatePriceName = (id: string, name: string) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        name: name || 'Default'
      }
    }));
  };

  const handleUpdatePrice = (id: string, price: number) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        price
      }
    }));
  };

  const handleUpdateDuration = (id: string, field: 'hours' | 'minutes', value: number) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        duration: {
          ...prev[id].duration,
          [field]: value
        }
      }
    }));
  };

  const handleDeletePrice = (id: string) => {
    if (Object.keys(priceOptions).length > 1) {
      const newPrices = { ...priceOptions };
      delete newPrices[id];
      setPriceOptions(newPrices);
    }
  };

  const handleEditAddon = (addon: ServiceAddon) => {
    setEditingAddon(addon.id);
    setEditedAddonData({
      name: addon.name,
      description: addon.description,
      price: addon.price,
      duration_minutes: addon.duration_minutes
    });
  };

  const handleSaveAddon = async (addonId: string) => {
    try {
      setLoading(prev => ({ ...prev, addons: true }));
      await updateServiceAddon(addonId, editedAddonData);
      await loadAddons();
      setEditingAddon(null);
      setEditedAddonData({});
    } catch (err) {
      console.error('Error updating add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to update add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  const handleDeleteAddon = async (addonId: string) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      await deleteServiceAddon(addonId);
      await loadAddons();
    } catch (err) {
      console.error('Error deleting add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, templates: true }));
      await deleteFlatRateTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };

  const handleCreateAddon = async (addonData: Partial<ServiceAddon>) => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      // TODO: Implement addon creation
      await loadAddons();
    } catch (err) {
      console.error('Error creating add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!service) return null;

  return (
    <div 
      className={`fixed inset-0 z-[10000] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`fixed inset-0 bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="h-screen flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[#2f2f2f] flex items-center justify-between bg-[#1a1a1a]">
            <div className="flex items-center space-x-4">
              <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
                <X className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-lg font-semibold text-white">{service?.name}</h2>
                <div className="text-sm text-gray-400">{service?.category?.name}</div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-[#f6f6f7]">
            <div className="max-w-4xl mx-auto py-6 px-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Basic Information */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-900">Service Information</h3>
                  <button
                    onClick={handleSaveInfo}
                    disabled={loading.info || !hasInfoChanges}
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md ${
                      hasInfoChanges && !loading.info 
                        ? 'bg-gray-900 hover:bg-gray-800'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {loading.info ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    >
                      {service.category && (
                        <option value={service.category.id}>{service.category.name}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Options - moved above Add-ons */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-900">Pricing Options</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleAddPrice}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleSavePricing}
                      disabled={loading.pricing || !hasPricingChanges}
                      className={`px-3 py-1.5 text-xs font-medium text-white rounded-md ${
                        hasPricingChanges && !loading.pricing
                          ? 'bg-gray-900 hover:bg-gray-800'
                          : 'bg-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {loading.pricing ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-gray-200">
                  {Object.entries(priceOptions).map(([id, data]) => (
                    <div key={id} className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <input
                          type="text"
                          value={data.name}
                          onChange={(e) => handleUpdatePriceName(id, e.target.value)}
                          className="text-sm font-medium text-gray-900 border border-gray-300 rounded-md px-2 py-1 focus:border-gray-900 focus:ring-gray-900"
                          placeholder="Enter name"
                        />
                        {Object.keys(priceOptions).length > 1 && (
                          <button
                            onClick={() => handleDeletePrice(id)}
                            className="p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Price
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              value={data.price}
                              onChange={(e) => handleUpdatePrice(id, parseFloat(e.target.value) || 0)}
                              className="w-full pl-7 pr-4 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Duration
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={data.duration.hours}
                              onChange={(e) => handleUpdateDuration(id, 'hours', parseInt(e.target.value) || 0)}
                              className="w-20 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                            />
                            <span className="text-sm text-gray-500">h</span>
                            <input
                              type="number"
                              value={data.duration.minutes}
                              onChange={(e) => handleUpdateDuration(id, 'minutes', parseInt(e.target.value) || 0)}
                              className="w-20 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                            />
                            <span className="text-sm text-gray-500">m</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-900">Add-ons</h3>
                  <button
                    onClick={() => setIsAddAddonModalOpen(true)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="divide-y divide-gray-200">
                  {loading.addons ? (
                    <div className="p-6 text-center text-sm text-gray-500">
                      Loading add-ons...
                    </div>
                  ) : addons.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500">
                      No add-ons available
                    </div>
                  ) : (
                    addons.map((addon) => (
                      <div key={addon.id} className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          {editingAddon === addon.id ? (
                            <input
                              type="text"
                              value={editedAddonData.name || ''}
                              onChange={(e) => setEditedAddonData(prev => ({ ...prev, name: e.target.value }))}
                              className="text-sm font-medium text-gray-900 border border-gray-300 rounded-md px-2 py-1 focus:border-gray-900 focus:ring-gray-900"
                              placeholder="Enter name"
                            />
                          ) : (
                            <div className="text-sm font-medium text-gray-900">
                              {addon.name}
                            </div>
                          )}
                          <div className="flex items-center space-x-2">
                            {editingAddon === addon.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveAddon(addon.id)}
                                  className="px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingAddon(null);
                                    setEditedAddonData({});
                                  }}
                                  className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditAddon(addon)}
                                  className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAddon(addon.id)}
                                  className="p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          {editingAddon === addon.id ? (
                            <>
                              <textarea
                                value={editedAddonData.description || ''}
                                onChange={(e) => setEditedAddonData(prev => ({ ...prev, description: e.target.value }))}
                                rows={2}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                                placeholder="Enter description"
                              />
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Price
                                  </label>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <span className="text-gray-500 sm:text-sm">$</span>
                                    </div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editedAddonData.price || ''}
                                      onChange={(e) => setEditedAddonData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                      className="w-full pl-7 pr-4 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Duration (hours)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={editedAddonData.duration_minutes ? editedAddonData.duration_minutes / 60 : ''}
                                    onChange={(e) => setEditedAddonData(prev => ({ 
                                      ...prev, 
                                      duration_minutes: Math.round(parseFloat(e.target.value || '0') * 60)
                                    }))}
                                    className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-sm text-gray-500">
                                {addon.description}
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center text-sm text-gray-500">
                                  <Clock className="h-4 w-4 mr-1.5" />
                                  {addon.duration_minutes >= 1440 
                                    ? `${Math.round(addon.duration_minutes / 1440)} days`
                                    : `${Math.round(addon.duration_minutes / 60)} hours`
                                  }
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  ${addon.price.toLocaleString()}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Flat Rate Templates section - moved below Add-ons */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-900">Flat Rate Templates</h3>
                  <button
                    onClick={() => setIsFlatRateModalOpen(true)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="divide-y divide-gray-200">
                  {loading.templates ? (
                    <div className="p-6 text-center text-sm text-gray-500">
                      Loading templates...
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500">
                      No templates available
                    </div>
                  ) : (
                    templates.map((template) => (
                      <div key={template.id} className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-sm font-medium text-gray-900">
                            {template.title}
                          </div>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1.5" />
                              {template.hours} hours
                            </div>
                            {template.rate_per_hour && (
                              <div className="font-medium text-gray-900">
                                ${template.rate_per_hour}/hr
                              </div>
                            )}
                          </div>
                          {template.description && (
                            <div className="text-sm text-gray-500">
                              {template.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {service?.id && (
        <>
          <FlatRateHoursModal 
            isOpen={isFlatRateModalOpen}
            onClose={() => setIsFlatRateModalOpen(false)}
            serviceId={service.id}
            onSuccess={() => {
              loadTemplates();
              onUpdate();
            }}
          />
          <AddAddonModal
            isOpen={isAddAddonModalOpen}
            onClose={() => setIsAddAddonModalOpen(false)}
            serviceId={service.id}
            onSuccess={() => {
              loadAddons();
              onUpdate();
            }}
          />
        </>
      )}
    </div>
  );
}